import { useCallback, useMemo, useState } from 'react';
import {
  INITIAL_SELECTION_STATE,
  reduceClear,
  reduceSelectAll,
  reduceToggle,
  reduceToggleRange,
  type SelectionState,
} from './task-selection-state';

/**
 * Per-page selection state for the task inbox bulk-action flow.
 *
 * Thin React wrapper over the pure reducers in
 * `./task-selection-state`. State transitions live there for unit
 * testing; this file just owns the `useState` cell + memoization.
 *
 * The hook is page-scoped — callers reset selection on filter / page
 * change by calling `clear()` from a useEffect on the relevant key.
 * That keeps the cap-of-100 contract honest (page is bounded) and
 * avoids the "selected on page 1, navigated to page 2, what now?"
 * edge case.
 */
export interface TaskSelectionApi {
  /** Currently selected task ids. */
  selected: Set<string>;
  /** Convenience: number of selected rows. */
  count: number;
  /** Whether a specific task is selected. */
  isSelected: (id: string) => boolean;
  /** Toggle a single row (single-click). Updates the range anchor. */
  toggle: (id: string, indexOnPage: number) => void;
  /** Range-select between the anchor and `indexOnPage` (shift-click). */
  toggleRange: (id: string, indexOnPage: number, allIdsOnPage: string[]) => void;
  /** Select every id on the current page. */
  selectAll: (idsOnPage: string[]) => void;
  /** Clear selection and the range anchor. */
  clear: () => void;
}

export function useTaskSelection(): TaskSelectionApi {
  const [state, setState] = useState<SelectionState>(INITIAL_SELECTION_STATE);

  const isSelected = useCallback((id: string) => state.selected.has(id), [state.selected]);

  const toggle = useCallback((id: string, indexOnPage: number) => {
    setState((prev) => reduceToggle(prev, id, indexOnPage));
  }, []);

  const toggleRange = useCallback((id: string, indexOnPage: number, allIdsOnPage: string[]) => {
    setState((prev) => reduceToggleRange(prev, id, indexOnPage, allIdsOnPage));
  }, []);

  const selectAll = useCallback((idsOnPage: string[]) => {
    setState((prev) => reduceSelectAll(prev, idsOnPage));
  }, []);

  const clear = useCallback(() => {
    setState(reduceClear());
  }, []);

  return useMemo(
    () => ({
      selected: state.selected,
      count: state.selected.size,
      isSelected,
      toggle,
      toggleRange,
      selectAll,
      clear,
    }),
    [state.selected, isSelected, toggle, toggleRange, selectAll, clear]
  );
}
